import type { LinkPreview } from "../data/demo";
import { reactive, watch } from "vue";
import type { MediaKind, UploadIntent } from "@desfecha/contracts";
import { api, apiUrl, ApiError, wsUrl } from "../api/client";
import type {
  ConversationDetail,
  ConversationRow,
  EventDto,
  Member,
  MessageDto,
  Note,
  PinnedDto,
  Outcome,
  RealtimeEvent,
  Team,
} from "../api/types";
import { toast } from "./feedback";
import { t } from "../i18n";

export type InboxView =
  | "mine"
  | "mentions"
  | "team"
  | "queue"
  | "automation"
  | "waiting_contact"
  | "snoozed"
  | "closed";
export const VIEWS: InboxView[] = [
  "mine",
  "mentions",
  "team",
  "queue",
  "automation",
  "waiting_contact",
  "snoozed",
  "closed",
];

export const inbox = reactive({
  slug: "",
  memberId: "",
  view: "mine" as InboxView,
  /** A tag chosen among the view chips, as WhatsApp's lists: its people instead of a view. */
  tagFilter: null as string | null,
  query: "",
  loading: false,
  error: "",
  conversations: [] as ConversationRow[],
  activeId: null as string | null,
  detail: null as ConversationDetail | null,
  messages: [] as MessageDto[],
  /** The conversation's pinned messages, newest pin first. */
  pinned: [] as PinnedDto[],
  events: [] as EventDto[],
  notes: [] as Note[],
  outcomes: [] as Outcome[],
  members: [] as Member[],
  teams: [] as Team[],
  connected: false,
  /** Per view, plus `attention`: what waits for the person (the tab's title). */
  counts: {} as Partial<Record<InboxView | "attention", number>>,
  /** Files on their way: shown as bubbles until the server message replaces them. */
  outgoing: [] as OutgoingMedia[],
});

export interface OutgoingMedia {
  key: string;
  conversationId: string;
  file: File;
  intent: UploadIntent;
  kind: MediaKind;
  caption: string;
  name: string;
  sizeBytes: number;
  previewUrl: string | null;
  thumbUrl: string | null;
  pages: number | null;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  createdAt: string;
  progress: number;
  status: "waiting" | "uploading" | "sending" | "failed" | "sent";
  error: string | null;
  /** Set once the server accepted it; the bubble goes away when that message is listed. */
  messageId: string | null;
}

export interface UploadDto {
  id: string;
  kind: MediaKind;
  mime: string;
  sizeBytes: number;
  filename: string | null;
  voice?: boolean;
  conversion?: string;
}

let socket: WebSocket | null = null;
/** Events sent while the connection was down are lost: coming back, the screen reloads. */
let connectedBefore = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let listTimer: ReturnType<typeof setTimeout> | null = null;
let detailTimer: ReturnType<typeof setTimeout> | null = null;

const base = () => `/v1/c/${inbox.slug}`;

export async function enterWorkspace(
  slug: string,
  memberId: string,
): Promise<void> {
  if (inbox.slug === slug && socket) return;
  leaveWorkspace();
  inbox.slug = slug;
  inbox.memberId = memberId;
  await Promise.all([loadConversations(), loadReference()]);
  connect();
}

export function leaveWorkspace(): void {
  disconnect();
  inbox.slug = "";
  // Tags belong to a company: another company has none of them.
  inbox.tagFilter = null;
  inbox.conversations = [];
  inbox.activeId = null;
  inbox.detail = null;
  inbox.messages = [];
  inbox.events = [];
  inbox.notes = [];
}

export async function loadReference(): Promise<void> {
  const [o, m, tm] = await Promise.all([
    api<{ outcomes: Outcome[] }>(`${base()}/outcomes`),
    api<{ members: Member[] }>(`${base()}/members`).catch(() => ({
      members: [] as Member[],
    })),
    api<{ teams: Team[] }>(`${base()}/teams`).catch(() => ({
      teams: [] as Team[],
    })),
  ]);
  inbox.outcomes = o.outcomes;
  inbox.members = m.members;
  inbox.teams = tm.teams;
}

export async function loadCounts(): Promise<void> {
  if (!inbox.slug) return;
  try {
    const res = await api<{
      counts: Partial<Record<InboxView | "attention", number>>;
    }>(`${base()}/conversations/counts`);
    inbox.counts = res.counts;
  } catch {
    // Badges are a convenience; the list request already surfaces errors.
  }
}

export async function loadConversations(): Promise<void> {
  if (!inbox.slug) return;
  inbox.loading = true;
  inbox.error = "";
  void loadCounts();
  try {
    const res = await api<{ conversations: ConversationRow[] }>(
      `${base()}/conversations`,
      {
        query: {
          view: inbox.tagFilter ? "all" : inbox.view,
          tag: inbox.tagFilter ?? undefined,
          limit: 100,
          q: inbox.query.trim() || undefined,
        },
      },
    );
    inbox.conversations = res.conversations;
  } catch (err) {
    inbox.error = err instanceof Error ? err.message : "error";
  } finally {
    inbox.loading = false;
  }
}

let searchTimer: ReturnType<typeof setTimeout> | null = null;
/** Server-side search: debounced so typing does not flood the API. */
export function search(query: string): void {
  inbox.query = query;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => void loadConversations(), 250);
}

/** The header's tag button: the tag goes to the conversation's person (it follows them). */
export async function tag(tagId: string, on: boolean): Promise<void> {
  const id = inbox.activeId;
  if (!id) return;
  await api(`${base()}/conversations/${id}/contact-tags/${tagId}`, {
    method: on ? "PUT" : "DELETE",
  });
  await refreshDetail();
  scheduleList();
}

export async function setView(view: InboxView): Promise<void> {
  inbox.view = view;
  inbox.tagFilter = null;
  await loadConversations();
}

/** Shows a tag's people, as a WhatsApp list; null goes back to the view. */
export async function setTagFilter(tagId: string | null): Promise<void> {
  inbox.tagFilter = tagId;
  await loadConversations();
}

/**
 * One chat per person and number: when the person on screen starts a new atendimento, the list
 * shows it in place of the one open, and the chat moves on to it. Null when nothing changes.
 */
export function threadSuccessor(
  rows: ConversationRow[],
  open: Pick<
    ConversationRow,
    "id" | "createdAt" | "contact" | "channel"
  > | null,
): string | null {
  if (!open || rows.some((r) => r.id === open.id)) return null;
  const next = rows.find(
    (r) =>
      r.contact.id === open.contact.id &&
      r.channel.id === open.channel.id &&
      r.createdAt > open.createdAt,
  );
  return next?.id ?? null;
}

export async function openConversation(id: string | null): Promise<void> {
  inbox.activeId = id;
  if (!id) {
    inbox.detail = null;
    inbox.messages = [];
    inbox.events = [];
    inbox.notes = [];
    return;
  }
  await refreshDetail();
  const row = inbox.conversations.find((c) => c.id === id);
  if (row) row.unreadCount = 0;
}

export async function refreshDetail(): Promise<void> {
  const id = inbox.activeId;
  if (!id) return;
  const [res, notes] = await Promise.all([
    api<{
      conversation: ConversationDetail;
      messages: MessageDto[];
      pinned?: PinnedDto[];
      events: EventDto[];
    }>(`${base()}/conversations/${id}`),
    api<{ notes: Note[] }>(`${base()}/conversations/${id}/notes`).catch(() => ({
      notes: [] as Note[],
    })),
  ]);
  if (inbox.activeId !== id) return;
  inbox.detail = res.conversation;
  inbox.messages = res.messages;
  inbox.pinned = res.pinned ?? [];
  inbox.events = res.events;
  inbox.notes = notes.notes;
  pruneOutgoing();
}

export interface GateResolution {
  decisionId: string;
  action: "accepted" | "edited" | "overridden";
}
export async function sendText(
  text: string,
  replyToProviderMessageId?: string,
  gate?: GateResolution,
  linkPreview = false,
): Promise<void> {
  const id = inbox.activeId;
  if (!id) return;
  const r = await api<{ gate?: { verdict: string; reasons: string[] } }>(
    `${base()}/conversations/${id}/messages`,
    {
      method: "POST",
      body: {
        type: "text",
        text,
        ...(replyToProviderMessageId ? { replyToProviderMessageId } : {}),
        ...(linkPreview ? { linkPreview: true } : {}),
        ...(gate
          ? { gateDecisionId: gate.decisionId, gateAction: gate.action }
          : {}),
      },
    },
  );
  // "Só registrar": it went out as written, and the review's flag is the person's to know.
  if (r?.gate) toast(t("inbox.gate_logged"));
  await refreshDetail();
  scheduleList();
}

/** A link's preview for the composer: the page's title, description, site and picture. */
export async function previewLink(url: string): Promise<LinkPreview | null> {
  const r = await api<{ preview: LinkPreview | null }>(
    `${base()}/link-preview?url=${encodeURIComponent(url)}`,
  );
  return r.preview;
}

/** Multipart upload with progress (fetch has no upload progress). The intent field goes first. */
export function uploadFile(
  file: File,
  intent: UploadIntent = "media",
  onProgress?: (fraction: number) => void,
  meta: {
    width?: number | null;
    height?: number | null;
    durationMs?: number | null;
  } = {},
): Promise<UploadDto> {
  return uploadFileTo(inbox.slug, file, intent, onProgress, meta);
}

/** The same staging upload for a screen outside the inbox (a template's header sample). */
export function uploadFileTo(
  slug: string,
  file: File,
  intent: UploadIntent = "media",
  onProgress?: (fraction: number) => void,
  meta: {
    width?: number | null;
    height?: number | null;
    durationMs?: number | null;
  } = {},
): Promise<UploadDto> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", apiUrl(`/v1/c/${slug}/uploads`));
    xhr.withCredentials = true;
    xhr.responseType = "json";
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      const json = (xhr.response ?? {}) as Record<string, unknown>;
      if (xhr.status >= 200 && xhr.status < 300)
        resolve((json as { upload: UploadDto }).upload);
      else
        reject(
          new ApiError(
            xhr.status,
            String(json.error ?? `http_${xhr.status}`),
            json.details,
          ),
        );
    };
    xhr.onerror = () => reject(new ApiError(0, "network_error"));
    const form = new FormData();
    form.append("intent", intent);
    for (const [name, value] of Object.entries(meta))
      if (value) form.append(name, String(Math.round(value)));
    form.append("file", file, file.name);
    xhr.send(form);
  });
}

export async function sendMedia(
  uploadId: string,
  caption?: string,
  conversationId = inbox.activeId,
): Promise<{ id: string } | null> {
  if (!conversationId) return null;
  const res = await api<{ message: { id: string } }>(
    `${base()}/conversations/${conversationId}/messages`,
    {
      method: "POST",
      body: { type: "media", uploadId, ...(caption ? { caption } : {}) },
    },
  );
  return res.message;
}

export interface MediaToSend {
  file: File;
  intent: UploadIntent;
  kind: MediaKind;
  caption: string;
  name: string;
  previewUrl: string | null;
  thumbUrl: string | null;
  pages: number | null;
  durationMs: number | null;
  width?: number | null;
  height?: number | null;
}

let outgoingSeq = 0;
const UPLOAD_PARALLEL = 3;

/**
 * Sends several files like WhatsApp Web: each shows at once as a bubble with progress, uploads
 * run a few at a time, and messages are created strictly in the chosen order (the worker
 * delivers them in that order too). A failed file does not stop the others.
 */
export async function sendMediaBatch(files: MediaToSend[]): Promise<void> {
  const conversationId = inbox.activeId;
  if (!conversationId || !files.length) return;
  const now = Date.now();
  const entries = files.map((f, i) => {
    inbox.outgoing.push({
      key: `out-${now.toString(36)}-${(outgoingSeq++).toString(36)}`,
      conversationId,
      file: f.file,
      intent: f.intent,
      kind: f.kind,
      caption:
        f.kind === "audio" || f.kind === "sticker" ? "" : f.caption.trim(),
      name: f.name,
      sizeBytes: f.file.size,
      previewUrl: f.previewUrl,
      thumbUrl: f.thumbUrl,
      pages: f.pages,
      durationMs: f.durationMs,
      width: f.width ?? null,
      height: f.height ?? null,
      createdAt: new Date(now + i).toISOString(),
      progress: 0,
      status: "waiting",
      error: null,
      messageId: null,
    });
    return inbox.outgoing[inbox.outgoing.length - 1]!;
  });
  const uploads = limited(entries, UPLOAD_PARALLEL, uploadEntry);
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]!;
    let uploadId: string;
    try {
      uploadId = await uploads[i]!;
    } catch (err) {
      fail(entry, err);
      continue;
    }
    await createEntryMessage(entry, uploadId);
  }
  await settle(conversationId);
}

/** Sends a failed message again (the server refuses when the failure needs someone to act). */
export async function retryMessage(messageId: string): Promise<void> {
  const id = inbox.activeId;
  if (!id) return;
  await api(`${base()}/conversations/${id}/messages/${messageId}/retry`, {
    method: "POST",
  });
  await refreshDetail();
  scheduleList();
}

/**
 * Our reaction on a message from the contact; an empty emoji takes it away. It shows at once,
 * as on WhatsApp, and the server's copy replaces the local one when the conversation reloads.
 */
export async function react(messageId: string, emoji: string): Promise<void> {
  const id = inbox.activeId;
  const target = inbox.messages.find((m) => m.id === messageId);
  if (!id || !target?.providerMessageId) return;
  const local: MessageDto = {
    id: `reaction-${messageId}-${Date.now()}`,
    direction: "outbound",
    author: { kind: "member", id: inbox.memberId || null },
    type: "reaction",
    body: null,
    content: {
      type: "reaction",
      reaction: { emoji, targetProviderMessageId: target.providerMessageId },
    },
    status: "queued",
    error: null,
    pricingCategory: null,
    sentAt: null,
    deliveredAt: null,
    readAt: null,
    createdAt: new Date().toISOString(),
  };
  inbox.messages.push(local);
  try {
    await api(`${base()}/conversations/${id}/messages/${messageId}/reaction`, {
      method: "PUT",
      body: { emoji },
    });
  } catch (err) {
    inbox.messages = inbox.messages.filter((m) => m.id !== local.id);
    throw err;
  }
  await refreshDetail();
}

/** Pins a message for the team, or unpins it; WhatsApp keeps three, a fourth replaces the oldest. */
export async function pin(messageId: string, pinned: boolean): Promise<void> {
  const id = inbox.activeId;
  if (!id) return;
  await api(`${base()}/conversations/${id}/messages/${messageId}/pin`, {
    method: "PUT",
    body: { pinned },
  });
  await refreshDetail();
}

/** Removes messages for the team (the contact keeps them); kept with who and when. */
export async function hideMessages(messageIds: string[]): Promise<number> {
  const id = inbox.activeId;
  if (!id || !messageIds.length) return 0;
  const r = await api<{ hidden: number }>(
    `${base()}/conversations/${id}/messages/hide`,
    { method: "POST", body: { messageIds } },
  );
  await refreshDetail();
  scheduleList();
  return r.hidden;
}

export async function restoreMessages(messageIds: string[]): Promise<void> {
  const id = inbox.activeId;
  if (!id || !messageIds.length) return;
  await api(`${base()}/conversations/${id}/messages/restore`, {
    method: "POST",
    body: { messageIds },
  });
  await refreshDetail();
  scheduleList();
}

export interface ForwardResult {
  sent: Array<{ conversationId: string; count: number; held: string[] }>;
  failed: Array<{ conversationId: string; error: string; count: number }>;
  noteId: string | null;
}
/** Forwards messages to other conversations and to colleagues. */
export async function forwardMessages(input: {
  messageIds: string[];
  conversationIds: string[];
  memberIds: string[];
  comment?: string;
}): Promise<ForwardResult> {
  const id = inbox.activeId;
  if (!id) return { sent: [], failed: [], noteId: null };
  const r = await api<ForwardResult>(`${base()}/conversations/${id}/forward`, {
    method: "POST",
    body: {
      messageIds: input.messageIds,
      conversationIds: input.conversationIds,
      memberIds: input.memberIds,
      ...(input.comment ? { comment: input.comment } : {}),
    },
  });
  await refreshDetail();
  scheduleList();
  return r;
}

/** Sends contacts of the workspace as WhatsApp contact cards. */
export async function sendContacts(contactIds: string[]): Promise<void> {
  const id = inbox.activeId;
  if (!id || !contactIds.length) return;
  await api(`${base()}/conversations/${id}/messages`, {
    method: "POST",
    body: { type: "contacts", contactIds },
  });
  await refreshDetail();
  scheduleList();
}

/** Tries a failed file again, alone. */
export async function retryOutgoing(key: string): Promise<void> {
  const entry = inbox.outgoing.find((o) => o.key === key);
  if (!entry || entry.status !== "failed") return;
  entry.error = null;
  entry.progress = 0;
  try {
    const uploadId = await uploadEntry(entry);
    await createEntryMessage(entry, uploadId);
  } catch (err) {
    fail(entry, err);
  }
  await settle(entry.conversationId);
}

export function discardOutgoing(key: string): void {
  const index = inbox.outgoing.findIndex((o) => o.key === key);
  if (index < 0) return;
  const [entry] = inbox.outgoing.splice(index, 1);
  releaseOutgoing(entry!);
}

async function uploadEntry(entry: OutgoingMedia): Promise<string> {
  entry.status = "uploading";
  const upload = await uploadFile(
    entry.file,
    entry.intent,
    (f) => {
      entry.progress = f;
    },
    { width: entry.width, height: entry.height, durationMs: entry.durationMs },
  );
  entry.progress = 1;
  return upload.id;
}

async function createEntryMessage(
  entry: OutgoingMedia,
  uploadId: string,
): Promise<void> {
  entry.status = "sending";
  try {
    const message = await sendMedia(
      uploadId,
      entry.caption || undefined,
      entry.conversationId,
    );
    entry.messageId = message?.id ?? null;
    entry.status = "sent";
    if (inbox.activeId === entry.conversationId) await refreshDetail();
  } catch (err) {
    fail(entry, err);
  }
  pruneOutgoing();
}

function fail(entry: OutgoingMedia, err: unknown): void {
  entry.status = "failed";
  entry.error = err instanceof ApiError ? err.code : "network_error";
}

async function settle(conversationId: string): Promise<void> {
  if (inbox.activeId === conversationId)
    await refreshDetail().catch(() => undefined);
  pruneOutgoing();
  scheduleList();
}

/** Drops bubbles whose server message is already listed (or sent in a conversation not open). */
function pruneOutgoing(): void {
  const listed = new Set(inbox.messages.map((m) => m.id));
  for (let i = inbox.outgoing.length - 1; i >= 0; i--) {
    const o = inbox.outgoing[i]!;
    const done =
      o.status === "sent" &&
      (!o.messageId ||
        listed.has(o.messageId) ||
        o.conversationId !== inbox.activeId);
    if (done) {
      inbox.outgoing.splice(i, 1);
      releaseOutgoing(o);
    }
  }
}

function releaseOutgoing(o: OutgoingMedia): void {
  // Object URLs belong to the composer's prepared files; revoking them here would break a
  // preview still on screen, so they are revoked only when no other bubble uses them.
  const inUse = inbox.outgoing.some(
    (x) => x.previewUrl === o.previewUrl || x.thumbUrl === o.thumbUrl,
  );
  if (inUse) return;
  for (const url of new Set([o.previewUrl, o.thumbUrl]))
    if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
}

/** Runs `task` over items with at most `limit` in flight; results keep the item order. */
function limited<T, R>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<R>,
): Promise<R>[] {
  const results: Promise<R>[] = [];
  let running = 0;
  const waiters: Array<() => void> = [];
  const acquire = () =>
    running < limit
      ? (running++, Promise.resolve())
      : new Promise<void>((resolve) =>
          waiters.push(() => (running++, resolve())),
        );
  const releaseSlot = () => {
    running--;
    waiters.shift()?.();
  };
  for (const item of items)
    results.push(acquire().then(() => task(item).finally(releaseSlot)));
  return results;
}

export async function sendTemplate(
  templateId: string,
  parameters: Record<string, unknown>,
  headerUploadId?: string,
  /** The person confirmed sending to a contact who paused active messages (decision 0036). */
  confirmHold?: boolean,
): Promise<void> {
  const id = inbox.activeId;
  if (!id) return;
  await api(`${base()}/conversations/${id}/messages`, {
    method: "POST",
    body: {
      type: "template",
      templateId,
      parameters,
      ...(headerUploadId ? { headerUploadId } : {}),
      ...(confirmHold ? { confirmHold: true } : {}),
    },
  });
  await refreshDetail();
  scheduleList();
}

export async function setPresence(
  presence: "online" | "paused" | "offline",
): Promise<void> {
  await api(`${base()}/members/${inbox.memberId}/presence`, {
    method: "PUT",
    body: { presence },
  });
  const me = inbox.members.find((m) => m.id === inbox.memberId);
  if (me) me.presence = presence;
}

async function action(path: string, body?: unknown): Promise<void> {
  const id = inbox.activeId;
  if (!id) return;
  await api(`${base()}/conversations/${id}/${path}`, {
    method: "POST",
    body: body ?? {},
  });
  await refreshDetail();
  await loadConversations();
}

export const take = () => action("take");
export const assign = (memberId: string | null, teamId?: string | null) =>
  action("assign", { memberId, teamId: teamId ?? null });
export const transfer = (input: {
  teamId?: string | null;
  memberId?: string | null;
  note?: string;
}) => action("transfer", input);
export const snooze = (until: Date, reason?: string) =>
  action("snooze", { until: until.toISOString(), reason });
export const wait = (on: "contact" | "internal") => action("wait", { on });
/** Concludes now, or only if the contact does not answer within `ifNoReplyHours`. */
export const close = (
  outcomeCode: string,
  note?: string,
  ifNoReplyHours?: number,
) =>
  action("close", {
    outcomeCode,
    note,
    ...(ifNoReplyHours ? { ifNoReplyHours } : {}),
  });
/** Calls a scheduled conclusion off; the conversation stays waiting for the contact. */
export async function cancelScheduledClose(): Promise<void> {
  const id = inbox.activeId;
  if (!id) return;
  await api(`${base()}/conversations/${id}/scheduled-close`, {
    method: "DELETE",
  });
  await refreshDetail();
  await loadConversations();
}
export const reopen = () => action("reopen");
export async function resume(): Promise<void> {
  // Leaving a waiting or snoozed state: assign to self keeps ownership and sets the status back to active.
  await assign(inbox.memberId);
}
export async function addNote(body: string): Promise<void> {
  const id = inbox.activeId;
  if (!id) return;
  await api(`${base()}/conversations/${id}/notes`, {
    method: "POST",
    body: { body },
  });
  await refreshDetail();
}

function scheduleList(): void {
  if (listTimer) clearTimeout(listTimer);
  listTimer = setTimeout(() => void loadConversations(), 250);
}
function scheduleDetail(): void {
  if (detailTimer) clearTimeout(detailTimer);
  detailTimer = setTimeout(() => void refreshDetail(), 150);
}

/** Other screens (Settings › Números) hear the workspace's events too. */
const listeners = new Set<(event: RealtimeEvent) => void>();
export function onRealtime(
  listener: (event: RealtimeEvent) => void,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function handle(event: RealtimeEvent): void {
  for (const listener of listeners) listener(event);
  if (event.type === "hello") return;
  if (event.type === "presence.changed") return;
  if (event.type === "number.updated") return;
  // The agenda listens for its own; the conversation it belongs to arrives as conversation.updated.
  if (event.type === "appointment.updated") return;
  // The import screen listens for its own; imported history is closed and needs no refresh.
  if (event.type === "import.updated") return;
  if (
    event.type === "note.mentioned" &&
    event.memberIds.includes(inbox.memberId) &&
    event.by !== inbox.memberId
  ) {
    const by = inbox.members.find((m) => m.id === event.by)?.name ?? "";
    toast(t("forward.received", { name: by }));
    void loadCounts();
  }
  scheduleList();
  if (
    inbox.activeId &&
    (event.conversationId === inbox.activeId || event.conversationId === "*")
  )
    scheduleDetail();
}

async function connect(): Promise<void> {
  if (!inbox.slug) return;
  try {
    const { ticket } = await api<{ ticket: string }>("/v1/auth/ws-ticket", {
      method: "POST",
    });
    const ws = new WebSocket(
      wsUrl(`${base()}/ws?ticket=${encodeURIComponent(ticket)}`),
    );
    socket = ws;
    ws.onopen = () => {
      inbox.connected = true;
      if (connectedBefore) {
        scheduleList();
        scheduleDetail();
      }
      connectedBefore = true;
    };
    ws.onmessage = (m) => {
      try {
        handle(JSON.parse(String(m.data)) as RealtimeEvent);
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => {
      inbox.connected = false;
      if (socket === ws) {
        socket = null;
        reconnectTimer = setTimeout(() => void connect(), 3000);
      }
    };
    ws.onerror = () => ws.close();
  } catch {
    reconnectTimer = setTimeout(() => void connect(), 5000);
  }
}

function disconnect(): void {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  connectedBefore = false;
  const ws = socket;
  socket = null;
  ws?.close();
  inbox.connected = false;
}

watch(
  () => inbox.query,
  () => scheduleList(),
);

/** The API already applied the search; kept as the single read point for the list. */
export function filteredConversations(): ConversationRow[] {
  return inbox.conversations;
}
